import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { USER_SUGGESTIONS_FOR_SEARCH } from "MVVM/GraphQL/userSuggestionsForSearch";
import { useUser } from "MVVM/Hooks/useUser";
import { isSuggestedUsersCacheStale } from "MVVM/Utilities/Cache";
import { UserSuggestionsForSearchState } from "MVVM/State/userSuggestionsForSearch";
import { ESuggestedFollowReason } from "MVVM/Views/Homepage/@types";

const LEGACY_REASON_MAPPING: Record<string, string> = {
  PLAYED_TOGETHER: "Played Together",
  FOLLOWED_BY_FOLLOWING: "Followed by Following",
  RISING_STAR: "Rising Star",
  UNSPECIFIED: "Unspecified",
  POPULAR_USER: "Popular User",
};

const mapReasonToDisplay = (reason: string): string => {
  if (LEGACY_REASON_MAPPING[reason]) {
    return LEGACY_REASON_MAPPING[reason];
  }

  if (ESuggestedFollowReason[reason as keyof typeof ESuggestedFollowReason]) {
    return ESuggestedFollowReason[
      reason as keyof typeof ESuggestedFollowReason
    ];
  }

  return ESuggestedFollowReason.UNSPECIFIED;
};

export const useUserSuggestionsForSearch = () => {
  const { allstarUser } = useUser();
  const loggedIn = allstarUser?.loggedIn;

  let useGraphQuery = loggedIn;

  const cacheKey = `${loggedIn ? "logged-in" : "logged-out"}_${
    useGraphQuery ? "graph" : "legacy"
  }_search_v1`;

  const userSuggestionsForSearchCacheState = useRecoilValue(
    UserSuggestionsForSearchState,
  );
  const setUserSuggestionsForSearchCache = useSetRecoilState(
    UserSuggestionsForSearchState,
  );

  const currentCacheState = userSuggestionsForSearchCacheState[cacheKey] || {
    suggestedFollowers: [],
    lastUpdated: 0,
  };

  const hasCachedData = currentCacheState.suggestedFollowers.length > 0;
  const isCacheStale = isSuggestedUsersCacheStale(
    currentCacheState.lastUpdated,
  );
  const shouldFetchFromNetwork = !hasCachedData || isCacheStale;

  const { data, error, loading } = useQuery(
    USER_SUGGESTIONS_FOR_SEARCH(useGraphQuery),
    {
      fetchPolicy: shouldFetchFromNetwork ? "network-only" : "cache-first",
      variables: useGraphQuery
        ? { count: 20, removeFollows: false }
        : { count: 20 },
      skip: !shouldFetchFromNetwork,
    },
  );

  const filteredSuggestedFollowers = useCallback(() => {
    const suggestedFollowers = data?.me?.suggestions;
    if (!suggestedFollowers) return [];

    const mappedSuggestions = suggestedFollowers.map((suggestion: any) => ({
      ...suggestion,
      reason: mapReasonToDisplay(suggestion.reason),
    }));

    return mappedSuggestions;
  }, [data?.me?.suggestions]);

  const suggestedFollowers = useMemo(() => {
    return filteredSuggestedFollowers();
  }, [filteredSuggestedFollowers]);

  useEffect(() => {
    if (suggestedFollowers && suggestedFollowers.length > 0) {
      const processUsersWithAvatars = async () => {
        const formattedFollowers = await Promise.all(
          suggestedFollowers.map(async (user: any) => {
            const u = user.user ? user.user : user;
            let avatar = u.avatar;

            if (!avatar) {
              try {
                const response = await fetch(
                  `${process.env.REACT_APP_AWS_SERVERLESS}/get-random-avatar`,
                );
                const data = await response.json();
                avatar = data.url;
              } catch (_e) {
                // Failed to fetch random avatar, using default
              }
            }

            return {
              _id: u._id,
              username: u.username || "User",
              avatar,
              reason: user.reason,
              steam: u.steam,
            };
          }),
        );

        setUserSuggestionsForSearchCache((prevState) => ({
          ...prevState,
          [cacheKey]: {
            suggestedFollowers: formattedFollowers,
            lastUpdated: Date.now(),
          },
        }));
      };

      processUsersWithAvatars();
    }
  }, [suggestedFollowers, setUserSuggestionsForSearchCache, cacheKey]);

  const finalSuggestedFollowers =
    !shouldFetchFromNetwork && !loading
      ? currentCacheState.suggestedFollowers.map((cachedUser) => {
          return {
            user: {
              _id: cachedUser._id,
              username: cachedUser.username,
              avatar: cachedUser.avatar,
              steam: cachedUser.steam,
            },
            reason: cachedUser.reason || "Unspecified",
          };
        })
      : suggestedFollowers;

  return {
    suggestedFollowers: finalSuggestedFollowers,
    error,
    loading: shouldFetchFromNetwork ? loading : false,
    isCacheStale,
    lastCacheUpdate: new Date(currentCacheState.lastUpdated),
  };
};
